import { useQuery } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { apiGet } from "../../lib/api";
import { cn } from "../../lib/utils";
import { type EtatTaverne, etatTaverneSchema } from "../contract";
import {
  ACCUEIL,
  BANDEAU_ALT,
  BANDEAU_SRC,
  CARTES,
  type CodeEtat,
  DISCORD_INVITATION,
  ETATS,
  heureDeParis,
  ORDRE_DES_CARTES,
  sousTitreDirect,
  TWITCH_CHAINE,
} from "./accueil-texts";
import { LIEN_REJOINDRE } from "./entree-texts";

/**
 * L'accueil public de La Taverne (`/`), d'apres les planches validees par Nicolas (bureau 1440 px, telephone 390 px).
 * La hauteur du bandeau au bureau n'est jamais fixe (`min-h`, l'image est en absolu derriere le texte) : un titre
 * Twitch de 140 caracteres fait grandir le bandeau, il ne rogne jamais les boutons.
 * Une seule mise en page qui passe de l'une a l'autre a 768 px : au telephone l'image est au-dessus et le texte
 * remonte sur son bas fondu ; au bureau l'image est derriere le texte, sous un degrade qui part de la gauche.
 *
 * Le visiteur ne voit jamais d'ecran d'erreur : tant que la reponse n'est pas arrivee, ou si elle echoue, la porte
 * est « close ». L'en-tete et le pied de page (liens legaux compris) sont ceux du template (`root-route.tsx`).
 *
 * Couleurs : les tokens du theme (`theme.css`) partout ou il y en a. Celles de la maquette qui n'en ont pas sont
 * nommees ci-dessous, en classes ecrites en toutes lettres (Tailwind ne genere que ce qu'il lit).
 */

/** Le rafraichissement de l'etat : le serveur le garde 30 s en cache, une minute suffit a un visiteur. */
export const REFRESH_MS = 60_000;

/** Le texte courant, un lilas clair (`#E6DAF0`). */
const TEXTE_DOUX = "text-[#E6DAF0]";
/** Le titre, un or pale (`#FCDD8E`, le haut de la rampe), et le contour du bouton principal. */
const TITRE_OR_PALE = "text-[#FCDD8E]";
const CONTOUR_OR_PALE = "border-[#FCDD8E]";
/** Le contour du badge hors ligne et du bouton Discord, un mauve (`#7F6AA8`). */
const CONTOUR_MAUVE = "border-[#7F6AA8]";
/** L'ombre portee du titre, la prune de la palette (`#6E3570`). */
const OMBRE_DU_TITRE = "[text-shadow:0_3px_0_#6E3570]";
/** Le fond des cartes au bureau : des bandes de 6 px, le fond puis un violet a peine plus clair (`#211D40`). */
const FOND_EN_BANDES =
  "md:bg-[repeating-linear-gradient(180deg,var(--background)_0_6px,#211D40_6px_12px)]";

/** La couleur de l'etat d'une carte : un corail clair, un lilas, un vert menthe. */
const COULEUR_ETAT: Record<CodeEtat, string> = {
  bientot: "text-[#F4A08E]",
  preparation: "text-[#BFAFD6]",
  ouvert: "text-[#7FD3A6]",
};

/** Ce que tous les liens-boutons partagent : 44 px au moins, anneau de focus du template. */
const BOUTON =
  "flex min-h-[52px] items-center justify-center rounded-[4px] border-2 px-[26px] text-center no-underline transition " +
  "hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-background md:min-h-[56px]";

/** L'etat a montrer : celui de l'API, ou « close » tant qu'elle n'a pas repondu et quand elle echoue. */
function useEtatTaverne(): EtatTaverne {
  const requete = useQuery({
    queryKey: ["taverne", "etat"],
    queryFn: () => apiGet("/api/taverne/etat", etatTaverneSchema),
    refetchInterval: REFRESH_MS,
  });
  if (requete.isError || requete.data === undefined) {
    return { enDirect: false, titre: null, jeu: null, depuis: null };
  }
  return requete.data;
}

export function AccueilPage(): ReactElement {
  const etat = useEtatTaverne();
  const sousTitre = etat.enDirect
    ? sousTitreDirect(etat.jeu, heureDeParis(etat.depuis))
    : ACCUEIL.horsLigne.sousTitre;
  const titre = etat.enDirect
    ? (etat.titre ?? ACCUEIL.direct.titreParDefaut)
    : ACCUEIL.horsLigne.titre;

  return (
    <div className="police-texte flex flex-col text-[22px] md:text-[27px]">
      <section className="flex flex-col overflow-hidden md:relative md:min-h-[500px] md:rounded-md md:border-b-[3px] md:border-[var(--lisere)]">
        <div className="relative h-[260px] overflow-hidden md:absolute md:inset-0 md:h-auto">
          <img
            src={BANDEAU_SRC}
            alt={BANDEAU_ALT}
            width={1652}
            height={480}
            className="block h-full w-full object-cover object-[82%_30%] md:object-[50%_40%]"
          />
          <div
            aria-hidden="true"
            className={cn(
              "absolute inset-0 bg-linear-to-b from-transparent from-40% to-background",
              "md:bg-linear-to-r md:from-[color-mix(in_srgb,var(--nuit)_92%,transparent)] md:from-0%",
              "md:via-[color-mix(in_srgb,var(--nuit)_70%,transparent)] md:via-38% md:to-transparent md:to-62%",
            )}
          />
        </div>

        <div className="relative -mt-7 flex flex-col gap-3.5 px-4 pb-6 md:mt-0 md:w-[752px] md:max-w-full md:gap-5 md:p-14">
          {etat.enDirect ? (
            <span className="police-nom flex items-center gap-2 self-start rounded-[4px] border-2 border-primary bg-[var(--gemme)] px-3 py-1 text-[19px] leading-none whitespace-nowrap min-[380px]:text-[28px] text-background md:gap-3 md:px-4 md:py-1.5 md:text-[37px]">
              <span aria-hidden="true" className="size-2.5 rounded-full bg-background md:size-3" />
              {ACCUEIL.direct.badge}
            </span>
          ) : (
            <span
              className={cn(
                "police-nom self-start rounded-[4px] border-2 bg-muted px-3 py-1 text-[19px] leading-none whitespace-nowrap min-[380px]:text-[28px] text-muted-foreground md:px-4 md:py-1.5 md:text-[37px]",
                CONTOUR_MAUVE,
              )}
            >
              {ACCUEIL.horsLigne.badge}
            </span>
          )}

          <h1
            className={cn(
              "police-titre m-0 text-[34px] leading-[1.1] md:text-[64px] md:leading-[1.05]",
              TITRE_OR_PALE,
              OMBRE_DU_TITRE,
            )}
          >
            {titre}
          </h1>
          {sousTitre === "" ? null : <p className={cn("m-0", TEXTE_DOUX)}>{sousTitre}</p>}

          <div className="mt-1 flex flex-col gap-3.5 md:mt-2 md:flex-row md:gap-4">
            <a
              href={TWITCH_CHAINE}
              rel="noopener"
              className={cn(BOUTON, CONTOUR_OR_PALE, "bg-primary text-primary-foreground")}
            >
              {etat.enDirect ? ACCUEIL.direct.boutonTwitch : ACCUEIL.horsLigne.boutonTwitch}
            </a>
            <a
              href={DISCORD_INVITATION}
              rel="noopener"
              className={cn(BOUTON, "bg-muted text-foreground", CONTOUR_MAUVE)}
            >
              {ACCUEIL.boutonDiscord}
            </a>
          </div>
          <a
            href="/rejoindre"
            className={cn(
              "flex min-h-11 items-center self-start underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              TITRE_OR_PALE,
            )}
          >
            {LIEN_REJOINDRE}
          </a>
        </div>
      </section>

      <div
        className={cn(
          "mt-2 grid grid-cols-1 gap-4 pt-2 pb-7 md:mt-0 lg:grid-cols-3 md:gap-8 md:rounded-b-md md:px-14 md:pt-11 md:pb-11",
          FOND_EN_BANDES,
        )}
      >
        {ORDRE_DES_CARTES.map((code) => {
          const carte = CARTES[code];
          return (
            <section
              key={code}
              className="flex flex-col gap-1.5 rounded-md border-2 border-[var(--lisere)] bg-card p-[18px] shadow-[inset_0_0_0_2px_var(--background),inset_0_0_0_3px_var(--border)] md:gap-2.5 md:px-7 md:py-[26px] md:shadow-[inset_0_0_0_2px_var(--background),inset_0_0_0_3px_var(--border),0_6px_0_var(--nuit)]"
            >
              <h2 className="police-titre m-0 text-[26px] text-primary md:text-[32px]">
                {carte.titre}
              </h2>
              {carte.texteTel === undefined ? (
                <p className={cn("m-0 text-[20px] md:text-[24px]", TEXTE_DOUX)}>{carte.texte}</p>
              ) : (
                <>
                  <p className={cn("m-0 hidden text-[24px] md:block", TEXTE_DOUX)}>{carte.texte}</p>
                  <p className={cn("m-0 text-[20px] md:hidden", TEXTE_DOUX)}>{carte.texteTel}</p>
                </>
              )}
              <span
                className={cn("text-[18px] md:mt-auto md:text-[20px]", COULEUR_ETAT[carte.etat])}
              >
                {ETATS[carte.etat]}
              </span>
            </section>
          );
        })}
      </div>
    </div>
  );
}
